import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import Navbar from "../Navbar";
import Footer from "../Footer";
import OutletSearch from "../SelectSearch/OutletSearch";
import { YearlyChart } from "./YearlyChart";
import { WeeklyChart } from "./WeeklyChart";
import PerformanceLineChart from "./PerformanceLineChart";

const Performance = () => {
  const url = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [token, setToken] = useState("");
  const [selectedOutlet, setSelectedOutlet] = useState(null);

  const [listYear, setListYear] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  const [dailyTotal, setDailyTotal] = useState([]);
  const [dailyLabel, setDailyLabel] = useState([]);
  const [monthlyLabel, setMonthlyLabel] = useState([]);
  const [monthlyTotal, setMonthlyTotal] = useState([]);

  const [chartShow, setShowChart] = useState(false);
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);

  const [yearError, setYearError] = useState(null);
  const [yearsListError, setYearsListError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);

  useEffect(() => {
    const tokenCheck = localStorage.getItem("token");
    if (!tokenCheck) navigate("/");
    else setToken(tokenCheck);
  }, [navigate]);

  useEffect(() => {
    if (!selectedOutlet) return;

    const fetchYearlyData = async () => {
      try {
        setLoading(true);
        setYearError(null);

        const res = await axios.post(`${url}/compareyearchart`, {
          outlet: selectedOutlet,
          token,
        });

        setData(res.data || []);
      } catch (err) {
        setYearError("Failed to load yearly performance data");
      } finally {
        setLoading(false);
      }
    };

    fetchYearlyData();
  }, [selectedOutlet, url, token]);

  useEffect(() => {
    if (!selectedOutlet) return;

    const fetchYears = async () => {
      try {
        setYearsListError(null);

        const res = await axios.post(`${url}/years`, {
          outlet: selectedOutlet,
          token,
        });

        const formatted = (res.data || []).map((y) => ({
          label: y.name.toString(),
          value: y.value,
        }));

        setListYear(formatted);
      } catch (err) {
        setYearsListError("Failed to load years");
        setListYear([]);
      }
    };

    fetchYears();
  }, [selectedOutlet, url, token]);

  const showChart = async () => {
    if (!selectedOutlet || !selectedYear) return;

    try {
      setLoading(true);
      setSummaryError(null);
      setShowChart(true);

      const res = await axios.post(`${url}/chartsummary`, {
        outlet: selectedOutlet,
        date: selectedYear.value,
        type: "yearly",
        token,
      });

      if (res?.data) {
        setDailyLabel(res.data.dailylabel);
        setDailyTotal(res.data.dailytotal);
        setMonthlyLabel(res.data.monthlylabel);
        setMonthlyTotal(res.data.monthlytotal);
      }
    } catch (err) {
      setSummaryError("Failed to load chart summary");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      {/* 🔍 Filters */}
      <div className="max-w-5xl mx-auto w-full p-4">
        <div className="bg-white shadow-md rounded-xl p-6 flex justify-between items-center">

          <div>
            <OutletSearch
              selectedOutlet={selectedOutlet}
              setSelectedOutlet={setSelectedOutlet}
              token={token}
            />
          </div>

          <div className="w-[200px]">
            <Select
              value={selectedYear}
              onChange={(option) => {
                setSelectedYear(option);

                if (!option) {
                  setShowChart(false);
                }
              }}
              options={listYear}
              placeholder="Select Year"
              isSearchable
              isClearable
              className="text-sm w-full"
            />
            {yearsListError && (
              <p className="text-red-500 text-xs mt-1">{yearsListError}</p>
            )}
          </div>

          <button
            onClick={showChart}
            disabled={!selectedOutlet || !selectedYear}
            className="px-4 py-2 rounded-md font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm transition"
          >
            Show Chart
          </button>
        </div>
      </div>

      {/* 📊 Charts */}
      <div className="flex-1 max-w-6xl mx-auto w-full py-4">

        {loading && (
          <div className="text-center text-gray-500 pb-4">Loading...</div>
        )}

        {yearError && (
          <div className="text-red-500 text-center pb-4">{yearError}</div>
        )}

        {summaryError && (
          <div className="text-red-500 text-center pb-4">{summaryError}</div>
        )}

        {!loading && !chartShow && (
          <div className="grid md:grid-cols-2 gap-6">
            <YearlyChart data={data} />
            <WeeklyChart data={data} />
          </div>
        )}

        {!loading && chartShow && (
          <div className="grid md:grid-cols-2 gap-6">
            <PerformanceLineChart
              labels={monthlyLabel}
              Total={monthlyTotal}
              labelTitle="Month"
              Title="Monthly Performance"
            />

            <PerformanceLineChart
              labels={dailyLabel}
              Total={dailyTotal}
              labelTitle="WeekDay"
              Title="Yearly Weekday Performance"
            />
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Performance;